import React, { lazy, useState, useEffect, useMemo, useRef } from "react";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CCardTitle,
  CSpinner,
  CTooltip,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { FiChevronDown, FiX } from "react-icons/fi";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import MainChartExample from "../charts/MainChartExample.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchList } from "src/store/actions/admin.js";
import _get from "lodash/get";
import _map from "lodash/map";
import moment from "moment";
import { searchCategories, searchUniqueSizes } from "src/utils";
import { getSubCategories, getDetailedSubCategories, getVariants } from "src/apis";
import { filterDropdownData } from "src/utils/helper";

const DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "this_week", label: "This week" },
  { id: "this_month", label: "This month" },
  { id: "last_2_months", label: "Last 2 months" },
];

const GROUP_BY_OPTIONS = [
  { id: "category", label: "Category" },
  { id: "type", label: "Detailed category" },
];

// Filter dropdown that looks like the date button: trigger is a button; search is inside the dropdown; does not close on select
const FilterButtonDropdown = ({
  placeholder,
  value = [],
  onChange,
  loadOptions,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const cacheRef = useRef({});

  const load = (term) => {
    const key = term || "";
    const cached = cacheRef.current[key];
    if (cached && Array.isArray(cached) && cached.length >= 0) {
      setOptions(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    loadOptions(key, (opts) => {
      const list = Array.isArray(opts) ? opts : [];
      cacheRef.current[key] = list;
      setOptions(list);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (open) setSearchInput("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = searchInput === "" ? 0 : 300;
    debounceRef.current = setTimeout(() => load(searchInput), delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, searchInput]);

  const toggleOption = (opt) => {
    const has = value.some((v) => v.value === opt.value);
    if (has) onChange(value.filter((v) => v.value !== opt.value));
    else onChange([...value, opt]);
  };

  const displayText = value.length > 0 ? `${value.length} selected` : placeholder;
  const hasSelection = value.length > 0;

  const handleClear = (e) => {
    e.stopPropagation();
    if (!disabled) onChange([]);
  };

  return (
    <div className="dashboard-filter-btn-wrapper">
      <div
        className={`dashboard-filter-btn ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && !disabled && setOpen((o) => !o)}
      >
        <span className="dashboard-filter-btn-text">{displayText}</span>
        <span className="dashboard-filter-btn-actions">
          {hasSelection && (
            <span
              className="dashboard-filter-btn-clear"
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
            >
              <FiX size={16} aria-hidden />
            </span>
          )}
          <FiChevronDown className="dashboard-filter-btn-chevron" size={16} />
        </span>
      </div>
      {open && (
        <>
          <div
            className="dashboard-filter-btn-backdrop"
            onClick={() => setOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close"
          />
          <div className="dashboard-filter-btn-popover">
            <input
              type="text"
              className="form-control form-control-sm dashboard-filter-btn-search"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="dashboard-filter-btn-options">
              {loading ? (
                <div className="dashboard-filter-btn-loading">Loading...</div>
              ) : options.length === 0 ? (
                <div className="dashboard-filter-btn-empty">No options</div>
              ) : (
                options.map((opt) => {
                  const isSelected = value.some((v) => v.value === opt.value);
                  return (
                    <div
                      key={opt.value}
                      className={`dashboard-filter-btn-option ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleOption(opt)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}
                    >
                      <input type="checkbox" checked={isSelected} readOnly className="mr-2" />
                      <span>{opt.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Format category path: only show " › " between segments that exist; no trailing arrow when detailed is empty
const formatCategoryPath = (fromApi, cat, sub, detailed) => {
  if (fromApi && typeof fromApi === "string") {
    return fromApi.replace(/\s*›\s*$/, "").trim();
  }
  const parts = [cat, sub, detailed].filter(Boolean);
  return parts.join(" › ");
};

const getDateRangeForPreset = (presetId) => {
  const today = moment();
  switch (presetId) {
    case "today":
      return [today.clone().startOf("day").toDate(), today.clone().endOf("day").toDate()];
    case "this_week":
      return [today.clone().startOf("week").toDate(), today.clone().endOf("week").toDate()];
    case "this_month":
      return [today.clone().startOf("month").toDate(), today.clone().endOf("month").toDate()];
    case "last_2_months": {
      const start = today.clone().subtract(2, "months").startOf("day");
      return [start.toDate(), today.clone().endOf("day").toDate()];
    }
    default:
      return null;
  }
};

const WidgetsDropdown = lazy(() => import("../widgets/WidgetsDropdown.js"));
// const WidgetsBrand = lazy(() => import("../widgets/WidgetsBrand.js"));

const Dashboard = () => {
  const dispatch = useDispatch();
  const [datePreset, setDatePreset] = useState("this_month");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const range = getDateRangeForPreset("this_month");
    return range || null;
  });
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [sizeFilter, setSizeFilter] = useState([]);
  const [groupBy, setGroupBy] = useState("category");
  const [defaultSizeColumns, setDefaultSizeColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const requestIdRef = useRef(0);
  const data = useSelector((s) => s.admin.dashboard?.data);
  const dashboardLoading = useSelector((s) => s.admin.dashboard?.loading);
  const flatList = _get(data, "items_sold_flat", []);
  const categoryBreakdown = _get(data, "category_breakdown", []);

  // Debounce search so we don't hit API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    getVariants({ limit: 500 })
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        const sizeSet = new Set();
        list.forEach((item) => {
          const s = item && item.size;
          if (s) sizeSet.add(s);
        });
        setDefaultSizeColumns(Array.from(sizeSet).sort());
      })
      .catch(() => setDefaultSizeColumns([]));
  }, []);

  const { sizeColumns, pivotRows, columnTotals, grandTotal } = useMemo(() => {
    const sizesInData = new Set();
    flatList.forEach((row) => {
      const s = _get(row, "size", "");
      if (s) sizesInData.add(s);
    });
    const baseList =
      defaultSizeColumns.length > 0 ? defaultSizeColumns : [...sizesInData].sort();
    const extraSizes = [...sizesInData].filter((s) => !baseList.includes(s)).sort();
    const defaultSizeCols = [...baseList, ...extraSizes].reverse();

    // When user has searched: always show Product + category path (item-style), ignore breakdown buttons
    if (debouncedSearch && debouncedSearch.trim() !== "") {
      const sizeColumns = defaultSizeCols;
      const keyToQuantities = {};
      const keyToLabels = {};
      flatList.forEach((row) => {
        const qty = Number(_get(row, "quantity", 0)) || 0;
        const size = _get(row, "size", "") || "";
        const cat = _get(row, "category_name", "");
        const sub = _get(row, "sub_category_name", "");
        const detailed = _get(row, "detailed_sub_category_name", "");
        const item = _get(row, "item_name", "");
        const categoryPath = _get(row, "category_path", "");
        const path = formatCategoryPath(categoryPath, cat, sub, detailed);
        const key = `${item}|${cat}|${sub}|${detailed}`;
        const label1 = item;
        const label2 = path;
        if (!keyToQuantities[key]) {
          keyToQuantities[key] = { _total: 0 };
          keyToLabels[key] = { label1, label2 };
        }
        keyToQuantities[key]._total += qty;
        if (!keyToQuantities[key][size]) keyToQuantities[key][size] = 0;
        keyToQuantities[key][size] += qty;
      });
      const pivotRows = Object.keys(keyToQuantities).map((key) => {
        const q = keyToQuantities[key];
        const { label1, label2 } = keyToLabels[key];
        const row = { key, label1, label2, total: q._total };
        sizeColumns.forEach((s) => (row[s] = q[s] || 0));
        return row;
      });
      pivotRows.sort((a, b) => b.total - a.total);
      const columnTotals = {};
      sizeColumns.forEach((s) => (columnTotals[s] = 0));
      let grandTotal = 0;
      pivotRows.forEach((r) => {
        sizeColumns.forEach((s) => (columnTotals[s] += r[s]));
        grandTotal += r.total;
      });
      return { sizeColumns, pivotRows, columnTotals, grandTotal };
    }

    // When "Category" breakdown and backend returned category_breakdown, use it
    if (groupBy === "category" && Array.isArray(categoryBreakdown) && categoryBreakdown.length > 0) {
      const sizeSet = new Set(defaultSizeCols);
      categoryBreakdown.forEach((row) => {
        const sizesObj = row.sizes || {};
        Object.keys(sizesObj).forEach((k) => k && sizeSet.add(k));
      });
      const sizeColumns = [...defaultSizeCols, ...[...sizeSet].filter((s) => !defaultSizeCols.includes(s)).sort()];
      const pivotRows = categoryBreakdown.map((row, i) => {
        const sizesObj = row.sizes || {};
        const key = String(_get(row, "category_id", i));
        const r = {
          key,
          label1: _get(row, "category_name", ""),
          label2: "",
          total: Number(_get(row, "total", 0)) || 0,
        };
        sizeColumns.forEach((s) => (r[s] = sizesObj[s] != null ? Number(sizesObj[s]) : 0));
        return r;
      });
      pivotRows.sort((a, b) => b.total - a.total);
      const columnTotals = {};
      sizeColumns.forEach((s) => (columnTotals[s] = 0));
      let grandTotal = 0;
      pivotRows.forEach((r) => {
        sizeColumns.forEach((s) => (columnTotals[s] += r[s] || 0));
        grandTotal += r.total;
      });
      return { sizeColumns, pivotRows, columnTotals, grandTotal };
    }

    const sizeColumns = defaultSizeCols;
    const keyToQuantities = {};
    const keyToLabels = {};

    flatList.forEach((row) => {
      const qty = Number(_get(row, "quantity", 0)) || 0;
      const size = _get(row, "size", "") || "";
      const cat = _get(row, "category_name", "");
      const sub = _get(row, "sub_category_name", "");
      const detailed = _get(row, "detailed_sub_category_name", "");
      const item = _get(row, "item_name", "");
      const categoryPath = _get(row, "category_path", "");

      let key;
      let label1;
      let label2;

      if (groupBy === "type") {
        const path = formatCategoryPath(categoryPath, cat, sub, detailed);
        key = path;
        label1 = path;
        label2 = "";
      } else {
        // groupBy === "category" (from flatList when not using category_breakdown)
        key = cat;
        label1 = cat;
        label2 = "";
      }

      if (!keyToQuantities[key]) {
        keyToQuantities[key] = { _total: 0 };
        keyToLabels[key] = { label1, label2 };
      }
      keyToQuantities[key]._total += qty;
      if (!keyToQuantities[key][size]) keyToQuantities[key][size] = 0;
      keyToQuantities[key][size] += qty;
    });

    const pivotRows = Object.keys(keyToQuantities).map((key) => {
      const q = keyToQuantities[key];
      const { label1, label2 } = keyToLabels[key];
      const row = { key, label1, label2, total: q._total };
      sizeColumns.forEach((s) => (row[s] = q[s] || 0));
      return row;
    });
    pivotRows.sort((a, b) => b.total - a.total);

    const columnTotals = {};
    sizeColumns.forEach((s) => (columnTotals[s] = 0));
    let grandTotal = 0;
    pivotRows.forEach((r) => {
      sizeColumns.forEach((s) => (columnTotals[s] += r[s]));
      grandTotal += r.total;
    });

    return { sizeColumns, pivotRows, columnTotals, grandTotal };
  }, [flatList, groupBy, defaultSizeColumns, categoryBreakdown, debouncedSearch]);

  useEffect(() => {
    const id = ++requestIdRef.current;
    setTableLoading(true);
    dispatch(
      fetchList("dashboard", {
        start_date: dateRange
          ? moment(dateRange?.[0]).format("YYYY-MM-DD")
          : "",
        end_date: dateRange ? moment(dateRange?.[1]).format("YYYY-MM-DD") : "",
        category_id: _map(categoryFilter, "value").filter(Boolean),
        sub_category_id: _map(subCategoryFilter, "value").filter(Boolean),
        detailed_sub_category_id: _map(typeFilter, "value").filter(Boolean),
        size: _map(sizeFilter, "value").filter(Boolean),
        search: debouncedSearch || "",
        group_by: groupBy === "category" ? "category" : "",
      })
    );
    const fallback = setTimeout(() => {
      if (requestIdRef.current === id) setTableLoading(false);
    }, 15000);
    return () => clearTimeout(fallback);
  }, [
    dispatch,
    dateRange,
    categoryFilter,
    subCategoryFilter,
    typeFilter,
    sizeFilter,
    debouncedSearch,
    groupBy,
  ]);

  useEffect(() => {
    if (data && typeof data === "object" && "items_sold_flat" in data) {
      setTableLoading(false);
    }
  }, [data]);

  const resetTableFilters = () => {
    setCategoryFilter([]);
    setSubCategoryFilter([]);
    setTypeFilter([]);
    setSizeFilter([]);
  };

  const resetAll = () => {
    setDateRange(null);
    setDatePreset("custom");
    setDatePickerOpen(false);
    resetTableFilters();
    setSearchQuery("");
  };

  // When search is active, show Product + path view; otherwise respect "Show breakdown by"
  const isSearchView = !!(debouncedSearch && debouncedSearch.trim() !== "");

  // Card stats: from same API response, filtered by date + category/sub/type/size (backend returns data.data[0])
  const cardStats = _get(data, "data[0]", {});

  return (
    <>
      {/* OLD: single date range row – replaced by presets + custom below
      Date Range
      <CRow className={"mb-4 mt-1"}>
        <CCol>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </CCol>
      </CRow>
      */}
      {/* OLD: date presets at top level – moved down into Items Sold card
      <CRow className="mb-2 mt-1">
        <CCol>
          <span className="mr-2">Date range:</span>
          <CButtonGroup>
            {DATE_PRESETS.map((preset) => (
              <CButton
                key={preset.id}
                color={datePreset === preset.id ? "primary" : "secondary"}
                variant={datePreset === preset.id ? undefined : "outline"}
                size="sm"
                onClick={() => {
                  setDatePreset(preset.id);
                  if (preset.id !== "custom") {
                    const range = getDateRangeForPreset(preset.id);
                    if (range) setDateRange(range);
                  }
                }}
              >
                {preset.label}
              </CButton>
            ))}
          </CButtonGroup>
        </CCol>
      </CRow>
      {datePreset === "custom" && (
        <CRow className="mb-4 mt-1">
          <CCol>
            <span className="mr-2">Start – End:</span>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </CCol>
        </CRow>
      )}
      */}
      {/* OLD: cards used first element of response (no explicit filter context)
      <WidgetsDropdown {..._get(data, "data[0]", {})} />
      */}
      {/* NEW: cards use cardStats (filtered by date + filters) */}
      <WidgetsDropdown {...cardStats} />
      <CCard>
        <CCardBody>
          <CRow>
            <CCol sm="5">
              <h4 id="traffic" className="card-title mb-0">
                Traffic
              </h4>
              <div className="small text-muted">November 2017</div>
            </CCol>
            <CCol sm="7" className="d-none d-md-block">
              <CButton color="primary" className="float-right">
                <CIcon name="cil-cloud-download" />
              </CButton>
              <CButtonGroup className="float-right mr-3">
                {["Day", "Month", "Year"].map((value) => (
                  <CButton
                    color="outline-secondary"
                    key={value}
                    className="mx-0"
                    active={value === "Month"}
                  >
                    {value}
                  </CButton>
                ))}
              </CButtonGroup>
            </CCol>
          </CRow>
          <MainChartExample style={{ height: "300px", marginTop: "40px" }} />
        </CCardBody>
      </CCard>

      {/* OLD: Items Sold table – commented for remake with intuitive/polish improvements
      <CRow> ... entire previous block ... </CRow>
      */}

      {/* NEW: Items Sold – client-focused, intuitive presentation */}
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <CCardTitle>Items Sold</CCardTitle>
            </CCardHeader>
            <CCardBody>
              {/* Row 1: Search bar (reduced width, no label, clear icon) + Reset all at extreme right */}
              <CRow className="mb-3 dashboard-search-row align-items-end">
                <CCol sm={9} md={8} className="dashboard-search-col">
                  <div className="dashboard-search-input-wrap">
                    <input
                      type="text"
                      className="form-control dashboard-search-input"
                      placeholder="Type to filter by product or item name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <span
                        className="dashboard-search-clear"
                        onClick={() => setSearchQuery("")}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setSearchQuery("")}
                        aria-label="Clear search"
                      >
                        <FiX size={16} />
                      </span>
                    )}
                  </div>
                </CCol>
                <CCol sm={3} md="auto" className="d-flex align-items-end dashboard-reset-col">
                  <CTooltip content="Clear date, all filters, and search.">
                    <span className="d-inline-block">
                      <CButton
                        color="primary"
                        variant="outline"
                        size="sm"
                        className="dashboard-reset-all-btn"
                        onClick={resetAll}
                      >
                        Reset all filters
                      </CButton>
                    </span>
                  </CTooltip>
                </CCol>
              </CRow>

              {/* Row 2: Date + Filter by (4 dropdowns) + Show breakdown by – one row, compact heights */}
              <CRow className="mb-3 dashboard-filters-row dashboard-filters-row-compact align-items-end">
                <CCol xs={12} sm={6} md={2} className="mb-2 mb-md-0 dashboard-date-col">
                  <label className="dashboard-filter-label">Date range</label>
                  <div className="dashboard-date-range-wrapper">
                    <div
                      className="dashboard-date-range-input dashboard-date-range-input-compact"
                      onClick={() => setDatePickerOpen((o) => !o)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setDatePickerOpen((o) => !o)}
                    >
                      <CIcon name="cil-calendar" className="mr-1" />
                      <span className="dashboard-date-range-text">
                        {dateRange && dateRange[0]
                          ? dateRange[0].getTime() === dateRange[1]?.getTime()
                            ? moment(dateRange[0]).format("MMM D, YYYY")
                            : `${moment(dateRange[0]).format("D MMM YYYY")} – ${moment(dateRange[1]).format("D MMM YYYY")}`
                          : "Select dates"}
                      </span>
                    </div>
                    {datePickerOpen && (
                      <>
                        <div
                          className="dashboard-date-range-backdrop"
                          onClick={() => setDatePickerOpen(false)}
                          role="button"
                          tabIndex={0}
                          aria-label="Close"
                        />
                        <div className="dashboard-date-range-popover">
                          <div className="dashboard-date-range-calendar-wrap">
                            <Calendar
                              value={dateRange || null}
                              onChange={(range) => {
                                if (!range) return;
                                const start = Array.isArray(range) ? range[0] : range;
                                const end = Array.isArray(range) && range[1] ? range[1] : start;
                                if (start) {
                                  setDateRange([start, end]);
                                  setDatePreset("custom");
                                  setDatePickerOpen(false);
                                }
                              }}
                              selectRange
                              prev2Label={null}
                              next2Label={null}
                            />
                          </div>
                          <div className="dashboard-date-presets">
                            <div className="dashboard-date-preset-grid">
                              {DATE_PRESETS.map((preset) => (
                                <CButton
                                  key={preset.id}
                                  color={datePreset === preset.id ? "primary" : "secondary"}
                                  variant={datePreset === preset.id ? undefined : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    setDatePreset(preset.id);
                                    const range = getDateRangeForPreset(preset.id);
                                    if (range) {
                                      setDateRange(range);
                                      setDatePickerOpen(false);
                                    }
                                  }}
                                >
                                  {preset.label}
                                </CButton>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CCol>
                <CCol xs={12} sm={12} md className="mb-2 mb-md-0 dashboard-filter-by-col dashboard-filter-by-col-grow">
                  <label className="dashboard-filter-label">Filter by</label>
                  <div className="d-flex flex-nowrap align-items-center dashboard-filter-group">
                    <div className="dashboard-filter-item">
                      <FilterButtonDropdown
                        placeholder="Category"
                        value={categoryFilter}
                        onChange={(v) => {
                          setCategoryFilter(v || []);
                          setSubCategoryFilter([]);
                          setTypeFilter([]);
                        }}
                        loadOptions={searchCategories}
                      />
                    </div>
                    <div className="dashboard-filter-item">
                      <FilterButtonDropdown
                        key={`subcat-${(categoryFilter || []).map((c) => c?.value).filter(Boolean).join("-") || "none"}`}
                        placeholder="Sub category"
                        value={subCategoryFilter}
                        onChange={(v) => {
                          setSubCategoryFilter(v || []);
                          setTypeFilter([]);
                        }}
                        loadOptions={(inputValue, callback) => {
                          const categoryIds = (categoryFilter || [])
                            .map((c) => c && c.value)
                            .filter(Boolean);
                          if (categoryIds.length === 0) {
                            callback([]);
                            return;
                          }
                          const search = inputValue || "";
                          Promise.all(
                            categoryIds.map((id) =>
                              getSubCategories({ search, id }).then((res) => {
                                const list = Array.isArray(res) ? res : res?.data || [];
                                return list;
                              })
                            )
                          )
                            .then((lists) => {
                              const seen = new Set();
                              const merged = [];
                              lists.forEach((list) => {
                                (list || []).forEach((item) => {
                                  const id = _get(item, "_id", "");
                                  if (id && !seen.has(id)) {
                                    seen.add(id);
                                    merged.push(item);
                                  }
                                });
                              });
                              callback(
                                filterDropdownData(merged, [
                                  "category_id.category_name",
                                  "sub_category_name",
                                ])
                              );
                            })
                            .catch(() => callback([]));
                        }}
                        disabled={categoryFilter.length === 0}
                      />
                    </div>
                    <div className="dashboard-filter-item">
                      <FilterButtonDropdown
                        key={`type-${(subCategoryFilter || []).map((c) => c?.value).filter(Boolean).join("-") || "none"}`}
                        placeholder="Detailed category"
                        value={typeFilter}
                        onChange={(v) => setTypeFilter(v || [])}
                        loadOptions={(inputValue, callback) => {
                          const subCategoryIds = (subCategoryFilter || [])
                            .map((c) => c && c.value)
                            .filter(Boolean);
                          if (subCategoryIds.length === 0) {
                            callback([]);
                            return;
                          }
                          const search = inputValue || "";
                          Promise.all(
                            subCategoryIds.map((sub_category_id) =>
                              getDetailedSubCategories({ search, sub_category_id }).then((res) => {
                                const list = Array.isArray(res) ? res : res?.data || [];
                                return list;
                              })
                            )
                          )
                            .then((lists) => {
                              const seen = new Set();
                              const merged = [];
                              lists.forEach((list) => {
                                (list || []).forEach((item) => {
                                  const id = _get(item, "_id", "");
                                  if (id && !seen.has(id)) {
                                    seen.add(id);
                                    merged.push(item);
                                  }
                                });
                              });
                              callback(
                                filterDropdownData(merged, [
                                  "category_id.category_name",
                                  "sub_category_id.sub_category_name",
                                  "detailed_sub_category_name",
                                ])
                              );
                            })
                            .catch(() => callback([]));
                        }}
                        disabled={subCategoryFilter.length === 0}
                      />
                    </div>
                    <div className="dashboard-filter-item">
                      <FilterButtonDropdown
                        placeholder="Size"
                        value={sizeFilter}
                        onChange={(v) => setSizeFilter(v || [])}
                        loadOptions={searchUniqueSizes}
                      />
                    </div>
                  </div>
                </CCol>
                {!isSearchView && (
                  <CCol xs={12} sm={6} md="auto" className="mb-2 mb-md-0 dashboard-group-by-col">
                    <label className="dashboard-filter-label">Show breakdown by</label>
                    <div className="dashboard-breakdown-buttons d-flex flex-wrap align-items-center">
                      {GROUP_BY_OPTIONS.map((opt) => (
                        <CButton
                          key={opt.id}
                          color={groupBy === opt.id ? "primary" : "secondary"}
                          variant={groupBy === opt.id ? undefined : "outline"}
                          size="sm"
                          className="dashboard-breakdown-btn mr-1"
                          onClick={() => setGroupBy(opt.id)}
                        >
                          {opt.label}
                        </CButton>
                      ))}
                    </div>
                  </CCol>
                )}
              </CRow>
              {/* <div className="small text-muted mb-2">Leave date empty for all time.</div> */}

              {/* Table: loader inside tbody; header and footer always visible */}
              <div className="table-responsive dashboard-items-table-wrapper">
                <table className="table table-bordered mb-0 d-none d-sm-table dashboard-items-table">
                  <thead>
                    <tr>
                      {isSearchView && <th className="sticky-left">Product</th>}
                      {!isSearchView && groupBy === "type" && <th className="sticky-left">Category path</th>}
                      {!isSearchView && groupBy === "category" && <th className="sticky-left">Category</th>}
                      {sizeColumns.map((s) => (
                        <th key={s} className="text-right">
                          {s}
                        </th>
                      ))}
                      <th className="text-right sticky-right">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <tr key={i} className="dashboard-table-skeleton-row">
                            <td colSpan={1 + sizeColumns.length + 1} className="align-middle py-3">
                              <div className="dashboard-table-skeleton-line" style={{ width: "100%" }} />
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : pivotRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={1 + sizeColumns.length + 1}
                          className="text-center text-muted py-5 dashboard-table-empty-cell"
                        >
                          No items sold for this period and filters. Try a different date
                          range or fewer filters.
                        </td>
                      </tr>
                    ) : (
                      pivotRows.map((row) => (
                        <tr key={row.key}>
                          {isSearchView && (
                            <td className="sticky-left">
                              <div>{row.label1}</div>
                              <div className="small text-muted">{row.label2}</div>
                            </td>
                          )}
                          {!isSearchView && groupBy === "type" && (
                            <td className="sticky-left">{row.label1}</td>
                          )}
                          {!isSearchView && groupBy === "category" && (
                            <td className="sticky-left">{row.label1}</td>
                          )}
                          {sizeColumns.map((s) => (
                            <td key={s} className="text-right">
                              {row[s] ? row[s] : ""}
                            </td>
                          ))}
                          <td className="text-right font-weight-bold sticky-right">
                            {row.total ? row.total : ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    {tableLoading ? (
                      <tr className="dashboard-table-skeleton-row">
                        <td colSpan={1 + sizeColumns.length + 1} className="align-middle py-3">
                          <div className="dashboard-table-skeleton-line" style={{ width: "100%" }} />
                        </td>
                      </tr>
                    ) : (
                      <tr
                        className="font-weight-bold"
                        style={{ borderTop: "2px solid #dee2e6" }}
                      >
                        <td
                          colSpan={1}
                          className="sticky-left"
                        >
                          TOTAL
                        </td>
                        {sizeColumns.map((s) => (
                          <td key={s} className="text-right">
                            {columnTotals[s] ? columnTotals[s] : ""}
                          </td>
                        ))}
                        <td className="text-right sticky-right">
                          {grandTotal ? grandTotal : ""}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                  </table>
                </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Old table – commented out */}
      {/* <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <CCardTitle>Top Categories - Items Sold </CCardTitle>
            </CCardHeader>
            <CCardBody>
              <table className="table table-hover table-outline mb-0 d-none d-sm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Category Name</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {_get(data, "get_category_product_detail", []).map(
                    (row, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{`${_get(row, "category_name", "-")} > ${_get(
                          row,
                          "sub_category_name",
                          "-"
                        )} > ${_get(
                          row,
                          "detailed_sub_category_name",
                          "-"
                        )}`}</td>
                        <td>{_get(row, "item_name")}</td>
                        <td>{_get(row, "quantity")}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow> */}
    </>
  );
};

export default Dashboard;
